import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import confetti from "canvas-confetti";
import { X, Loader2, Sparkles, Trophy } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { CoinIcon } from "@/components/CoinBadge";

const SEG_COLORS = ["#14B8A6", "#0B132B", "#0EA5A0", "#1F3A5F", "#14B8A6", "#F5B301", "#0B132B", "#0EA5A0"];
const CX = 150, CY = 150, R = 142;

const pt = (angle, radius) => {
  const rad = (angle * Math.PI) / 180;
  return [CX + radius * Math.sin(rad), CY - radius * Math.cos(rad)];
};

function Slice({ i, seg }) {
  const a0 = i * 45, a1 = i * 45 + 45, am = i * 45 + 22.5;
  const [x0, y0] = pt(a0, R);
  const [x1, y1] = pt(a1, R);
  const [lx, ly] = pt(am, R * 0.64);
  const gold = seg.bonus;
  const lose = seg.lose;
  const fill = lose ? "#334155" : SEG_COLORS[i % SEG_COLORS.length];
  const text = gold ? "#0B132B" : "#ffffff";
  return (
    <g>
      <path d={`M ${CX} ${CY} L ${x0} ${y0} A ${R} ${R} 0 0 1 ${x1} ${y1} Z`} fill={fill} stroke="#ffffff" strokeWidth="1.5" />
      <g transform={`rotate(${am} ${lx} ${ly})`}>
        {lose ? (
          <>
            <text x={lx} y={ly - 3} textAnchor="middle" fontWeight="800" fontSize="8.5" fill={text}>BETTER</text>
            <text x={lx} y={ly + 6} textAnchor="middle" fontWeight="800" fontSize="8.5" fill={text}>LUCK</text>
          </>
        ) : (
          <>
            <text x={lx} y={ly - 2} textAnchor="middle" fontWeight="800" fontSize="19" fill={text}>{seg.label}</text>
            <text x={lx} y={ly + 12} textAnchor="middle" fontWeight="700" fontSize="7.5" fill={text} opacity="0.85">COINS</text>
          </>
        )}
      </g>
    </g>
  );
}

export default function DailySpinWheel({ open, onClose, onPointsChange }) {
  const [loading, setLoading] = useState(true);
  const [segments, setSegments] = useState([]);
  const [points, setPoints] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [limit, setLimit] = useState(1);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null); // {points}
  const timer = useRef(null);
  const played = remaining <= 0;

  useEffect(() => {
    if (!open) return;
    window.history.pushState({ zpGame: true }, "");
    const onPop = () => onClose();
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [open]);
  const close = () => { if (window.history.state?.zpGame) window.history.back(); else onClose(); };

  const landRotation = (idx, animate) => {
    const base = animate ? 360 * 6 : 0;
    const jitter = animate ? Math.random() * 24 - 12 : 0;
    return base - (idx * 45 + 22.5) + jitter;
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true); setResult(null); setSpinning(false); setRotation(0);
    api.get("/games/daily-spin").then(({ data }) => {
      setSegments(data.segments || []);
      setPoints(data.points || 0);
      setLimit(data.limit ?? 1); setRemaining(data.remaining ?? 0);
      if ((data.remaining ?? 0) <= 0 && data.result) {
        setRotation(landRotation(data.result.segment_index, false));
        setResult({ points: data.result.points });
      }
    }).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [open]);

  const doSpin = async () => {
    if (spinning || played) return;
    setSpinning(true); setResult(null);
    try {
      const { data } = await api.post("/games/daily-spin/spin");
      setRotation(landRotation(data.segment_index, true));
      timer.current = setTimeout(() => {
        setSpinning(false);
        setRemaining(data.remaining ?? 0);
        setPoints(data.balance);
        onPointsChange && onPointsChange(data.balance);
        setResult({ points: data.points });
        if (data.points > 0) {
          confetti({ particleCount: 140, spread: 80, origin: { y: 0.4 }, colors: ["#14B8A6", "#F5B301", "#0EA5A0", "#ffffff"] });
        }
      }, 4300);
    } catch (e) {
      setSpinning(false);
      toast.error(apiError(e));
    }
  };

  if (!open) return null;

  const remainingLabel = played ? "No spins left today • come back tomorrow" : `${remaining} of ${limit} spin${limit > 1 ? "s" : ""} left today`;

  return createPortal((
    <div className="fixed inset-0 z-[100] flex flex-col bg-[#0B132B]" data-testid="daily-spin-modal">
      <div className="pointer-events-none absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 50% 25%, rgba(20,184,166,0.35), transparent 55%)" }} />
      <header className="relative flex items-center justify-between px-4 py-4 sm:px-8">
        <button onClick={close} data-testid="spin-back-btn" className="grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20">
          <X size={20} />
        </button>
        <div className="flex items-center gap-2 font-display text-lg font-bold text-white">
          <Sparkles size={18} className="text-[#F5B301]" /> Daily Spin
        </div>
        <div className="flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/10 px-3 py-1.5 text-right">
          <CoinIcon size={18} />
          <div><p className="text-[10px] uppercase tracking-wide text-white/50">Coins</p>
          <p data-testid="spin-points" className="-mt-0.5 font-display text-base font-bold text-white">{points.toLocaleString("en-IN")}</p></div>
        </div>
      </header>

      <div className="relative flex flex-1 flex-col items-center justify-center gap-6 px-4 pb-10">
        {loading ? (
          <Loader2 className="h-8 w-8 animate-spin text-[#14B8A6]" />
        ) : (
          <>
            <p className="max-w-sm text-center text-sm text-white/70">Spin for a chance to earn bonus ZRo Pe Coins. The winning segment is decided securely on our server.</p>
            <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white/90" data-testid="spin-remaining">{remainingLabel}</span>

            <div className="relative w-[min(82vw,360px)]" style={{ aspectRatio: "1 / 1" }}>
              <div className="absolute left-1/2 top-[-6px] z-20 -translate-x-1/2" style={{ filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.4))" }}>
                <svg width="34" height="30" viewBox="0 0 34 30"><path d="M17 30 L2 2 Q17 12 32 2 Z" fill="#F5B301" stroke="#fff" strokeWidth="1.5" /></svg>
              </div>
              <svg viewBox="0 0 300 300" className="h-full w-full drop-shadow-2xl">
                <circle cx={CX} cy={CY} r={R + 6} fill="#0B132B" stroke="#F5B301" strokeWidth="4" />
                <g style={{ transform: `rotate(${rotation}deg)`, transformOrigin: "150px 150px", transition: spinning ? "transform 4.2s cubic-bezier(0.17,0.67,0.15,0.99)" : "none" }}>
                  {segments.map((seg, i) => <Slice key={i} i={i} seg={seg} />)}
                </g>
                <circle cx={CX} cy={CY} r="34" fill="#ffffff" />
              </svg>
              <button
                onClick={doSpin}
                disabled={spinning || played}
                data-testid="spin-button"
                className="absolute left-1/2 top-1/2 z-10 grid h-[68px] w-[68px] -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-gradient-to-br from-[#14B8A6] to-[#0D9488] font-display text-sm font-extrabold text-white shadow-lg transition active:scale-95 disabled:opacity-70"
              >
                {spinning ? <Loader2 className="h-5 w-5 animate-spin" /> : (result && !played ? "SPIN AGAIN" : "SPIN")}
              </button>
            </div>

            <div className="min-h-[76px] text-center">
              {result ? (
                result.points > 0 ? (
                  <div data-testid="spin-result" className="animate-in fade-in zoom-in rounded-xl border border-[#F5B301]/40 bg-[#F5B301]/10 px-6 py-3 text-white">
                    <p className="flex items-center justify-center gap-2 font-display text-xl font-extrabold text-[#F5B301]"><Trophy size={20} /> Congratulations!</p>
                    <p className="mt-0.5 flex items-center justify-center gap-1 text-sm">You won <b>{result.points}</b> <CoinIcon size={16} /> ZRo Pe Coins!</p>
                    <p className="mt-1 text-xs text-white/60">{remainingLabel}</p>
                  </div>
                ) : (
                  <div data-testid="spin-result" className="rounded-xl bg-white/10 px-5 py-3 text-white">
                    <p className="font-display text-lg font-bold">No luck this time!</p>
                    <p className="text-xs text-white/60">{remainingLabel}</p>
                  </div>
                )
              ) : played ? (
                <div data-testid="spin-result" className="rounded-xl bg-white/10 px-5 py-3 text-white">
                  <p className="font-display text-lg font-bold">No spins left today</p>
                  <p className="text-sm text-white/70">Come back tomorrow for another chance!</p>
                </div>
              ) : (
                <p className="text-sm text-white/50">Press SPIN to try your luck!</p>
              )}
            </div>
            <button onClick={close} data-testid="spin-back-games" className="rounded-full bg-white/10 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-white/20">Back to Games</button>
          </>
        )}
      </div>
    </div>
  ), document.body);
}
